import {observer} from 'mobx-react-lite'
import * as React from 'react'
import {useEffect} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Backend} from '@common/backend'
import {AutoTaskCoverCheckbox} from '@components/board/general-board-settings-popup/auto-task-cover-checkbox'
import {LinkButton} from '@components/common/base/buttons'
import {CheckboxNew} from '@components/common/base/checkbox-new/checkbox-new'
import {useMainMenuStore} from '@components/main-menu/main-menu-store'
import {byTimeGlobalStore} from '@components/statistics/by-time/by-time-global-store'
import {useBoard, useMemoOne} from '@containers/hooks'
import {PageService} from '@extensions/page-service'
import {AutoDoneTaskCheckbox} from './auto-done-task-checkbox'
import {ExperimentalFeatures} from './experimental-features'
import {GeneralBoardSettingsPopupStore} from './generalboard-settings-popup-store'
import {MoveTaskToColCheckbox} from './move-task-to-col-checkbox'
import {useSyncBoard} from './use-sync-board'

const TwSettingsSection = tw.div`
  mb-16
  tracking-tighter
`

const TwSettingsSectionTitle = tw.div`
  mb-16
  text-primary
  text-sm-semibold
`

export const GeneralBoardSettingsInner = observer(() => {
  const [menuState] = useMainMenuStore()
  const store = useMemoOne(() => new GeneralBoardSettingsPopupStore(), [])
  const board = useBoard()

  useSyncBoard({boardId: board.id, store})

  useEffect(() => {
    store.setBoard(board.id)
    store.loadActualData()
  }, [board.id])

  const goToReport = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    byTimeGlobalStore.onSelectProject(PageService.getCurrentProject().id)
    byTimeGlobalStore.onSelectBoard(PageService.getCurrentBoard().id)
    PageService.goToStatistics('by-time')
  }

  return (
    <div className="ml-8 w-[552px]">
      <TwSettingsSection>
        <TwSettingsSectionTitle>
          {Lang.msg.board.automationSettings}
        </TwSettingsSectionTitle>

        <MoveTaskToColCheckbox
          setValue={store.commitMoveTaskToColWhenDone}
          onClick={store.onToggleMoveTaskToColWhenDone}
          data={store.state.moveTaskToColWhenDone}
          label={Lang.msg.board.moveTaskToCol}
          hintLabel={Lang.msg.board.moveTaskToColHint}
        />

        <AutoDoneTaskCheckbox
          setValue={store.commitAutoDoneTaskWhenMoveToCol}
          onClick={store.onToggleAutoDoneTaskWhenMoveToCol}
          data={store.state.autoDoneTaskWhenMoveToCol}
          board={board}
          label={Lang.msg.board.autoDoneTaskToCol}
          hintLabel={Lang.msg.board.autoDoneTaskToColHint}
        />

        <AutoTaskCoverCheckbox
          setValue={store.commitAutoTaskCover}
          onClick={store.onToggleAutoTaskCover}
          data={store.state.autoTaskCover}
          board={board}
          label={Lang.msg.board.autoTaskCover}
          hintLabel={Lang.msg.board.autoTaskCoverHint}
        />

        {Backend.features.autoCompleteTasks && (
          <CheckboxNew
            key={'autoCompleteTasks'}
            className="mb-16"
            value={Boolean(store.state.autoCompleteTasks)}
            onChange={store.onToggleAutoCompleteTasks}
            label={Lang.msg.board.autoCompleteTasks}
            footnote={Lang.msg.board.autoCompleteTasksHint}
            renderFullFootnote
          />
        )}
      </TwSettingsSection>
      <TwSettingsSection>
        <TwSettingsSectionTitle>
          {Lang.msg.board.viewSettingsTitle}
        </TwSettingsSectionTitle>

        <CheckboxNew
          className={'mb-16'}
          value={Boolean(store.state.showTimeTaskInColumn)}
          onChange={store.onToggleShowTimeTaskInColumn}
          label={Lang.msg.board.timeTaskInCol}
          footnote={
            <div className="flex items-center">
              <span>{Lang.msg.board.timeTaskInColNote}&nbsp;</span>
              {menuState.canSeeStatsPage && (
                <LinkButton
                  className="flex items-start !text-12"
                  noPaddings
                  onClick={goToReport}
                >
                  {Lang.msg.board.showReport}
                </LinkButton>
              )}
            </div>
          }
        />

        {Backend.features.counterInColumns && (
          <CheckboxNew
            key={'counterInColumns'}
            className="mb-16"
            value={Boolean(store.state.counterInColumns)}
            onChange={store.onToggleCounterInColumns}
            label={Lang.context.board.counterInColumns}
          />
        )}

        <CheckboxNew
          className="mb-16"
          value={Boolean(store.state.showCountMsgsInTask)}
          onChange={store.onToggleShowCountMsgsInTask}
          label={Lang.msg.board.countMsgInTask}
        />

        {store.state.showCountMsgsInTask && (
          <CheckboxNew
            className="mb-16 ml-24"
            value={Boolean(store.state.showZeroCountMsgsInTask)}
            onChange={store.onToggleShowZeroCountMsgsInTask}
            label={Lang.msg.board.showZeroMsgInTask}
          />
        )}

        {Backend.features.idTasksByProject && (
          <CheckboxNew
            className="mb-16"
            value={Boolean(store.state.showIdOnTaskCard)}
            onChange={store.onToggleShowIdOnTaskCard}
            label={Lang.msg.board.showIdOnTaskCard}
          />
        )}
      </TwSettingsSection>
      <ExperimentalFeatures />
    </div>
  )
})
